/**
 *  @class
 *  @function BackgroundVideo
 */
if (!customElements.get('background-video')) {
	class BackgroundVideo extends HTMLElement {
		constructor() {
			super();

			this.tl = false;
			this.splittext = false;
			this.video = null;
			this.video_container = null;
		}

		connectedCallback() {
			let _this = this;

			if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
				this.prepareAnimations();
			}

			// Video Support.
			this.video_container = this.querySelector('.background-video__iframe');
			if (this.video_container) {
				if (this.video_container.querySelector('iframe')) {
					let iframe = this.video_container.querySelector('iframe');
					iframe.onload = function() {
						_this.videoPlay(_this.video_container);
					};
					// In case already loaded
					if (iframe.contentWindow) {
						_this.videoPlay(_this.video_container);
					}
				}
				if (this.video_container.querySelector('video')) {
					this.setupHostedVideo(this.video_container.querySelector('video'));
				}
			}

			// Watch for external DOM reflows (e.g. Judge.me review pagination)
			this.setupPaginationWatcher();
		}

		disconnectedCallback() {
			if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
				if (this.tl) this.tl.kill();
				if (this.splittext) this.splittext.revert();
			}
			if (this.video && this.onPause) {
				this.video.removeEventListener('pause', this.onPause);
			}
			if (this.onFirstGesture) {
				document.removeEventListener('touchstart', this.onFirstGesture);
				document.removeEventListener('click', this.onFirstGesture);
			}
			if (this.playHostedVideo) {
				window.removeEventListener('pageshow', this.playHostedVideo);
				document.removeEventListener('visibilitychange', this.onVisibilityChange);
			}
			if (this.onDocumentClick) {
				document.removeEventListener('click', this.onDocumentClick);
			}
			if (this.onResize) {
				window.removeEventListener('resize', this.onResize);
			}
			if (this.intersectionObserver) {
				this.intersectionObserver.disconnect();
			}
			if (this.bodyMutationObserver) {
				this.bodyMutationObserver.disconnect();
			}
		}

		// iOS / macOS Safari needs muted + playsinline set as both attribute and property,
		// and silently rejects play() in Low Power Mode, after bfcache restore, or during reflows.
		setupHostedVideo(video) {
			this.video = video;
			video.muted = true;
			video.defaultMuted = true;
			video.playsInline = true;
			video.loop = true;
			video.setAttribute('muted', '');
			video.setAttribute('playsinline', '');
			video.setAttribute('webkit-playsinline', '');
			video.setAttribute('autoplay', '');
			video.setAttribute('loop', '');

			this.playHostedVideo = () => {
				if (!video) return;
				if (!video.paused) return;
				let playPromise = video.play();
				if (playPromise !== undefined) {
					playPromise.catch((e) => {
						// Autoplay might be temporarily blocked until user interaction or buffer ready
					});
				}
			};

			this.onFirstGesture = () => {
				this.playHostedVideo();
				document.removeEventListener('touchstart', this.onFirstGesture);
				document.removeEventListener('click', this.onFirstGesture);
			};

			this.onVisibilityChange = () => {
				if (document.visibilityState === 'visible') this.playHostedVideo();
			};

			// Safari on macOS / iOS may pause videos during scroll or DOM reflows (e.g. pagination)
			this.onPause = () => {
				if (document.visibilityState === 'visible') {
					this.playHostedVideo();
				}
			};
			video.addEventListener('pause', this.onPause);

			// Retry once the browser has enough data (first play() can fire too early)
			video.addEventListener('loadedmetadata', this.playHostedVideo, { once: true });
			video.addEventListener('canplay', this.playHostedVideo, { once: true });

			// Fallback for iOS Low Power Mode: autoplay is blocked until the first user gesture
			document.addEventListener('touchstart', this.onFirstGesture, { passive: true });
			document.addEventListener('click', this.onFirstGesture);

			// iOS Safari restores pages from bfcache with videos paused
			window.addEventListener('pageshow', this.playHostedVideo);
			document.addEventListener('visibilitychange', this.onVisibilityChange);

			// Resume playback and refresh ScrollTrigger when entering viewport
			if ('IntersectionObserver' in window) {
				this.intersectionObserver = new IntersectionObserver((entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.playHostedVideo();
							if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
								window.ScrollTrigger.refresh();
							}
						}
					});
				}, { rootMargin: '200px 0px', threshold: [0, 0.25, 0.5] });
				this.intersectionObserver.observe(this);
			}

			this.playHostedVideo();
		}

		handleReflow() {
			if (this.video) {
				this.playHostedVideo();
				// Force Safari GPU layer redraw to prevent tearing or blank frames
				this.video.style.transform = 'translate3d(0, 0, 0)';
			} else if (this.video_container) {
				this.videoPlay(this.video_container);
			}

			// Invalidate and refresh GSAP ScrollTrigger if present so lightbox button / animations remain synced
			if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
				window.ScrollTrigger.refresh();
			}
		}

		setupPaginationWatcher() {
			let _this = this;

			// Handle review pagination clicks or any pagination clicks on the page
			this.onDocumentClick = (e) => {
				const isPagination = e.target.closest(
					'.jdgm-paginate, .jdgm-paginate__page, .jdgm-paginate__next-page, .jdgm-paginate__prev-page, .jdgm-paginate__last-page, .pagination, [class*="paginate"]'
				);
				if (isPagination) {
					// Judge.me fetches via AJAX, replaces reviews HTML, and scrolls the page
					[50, 150, 300, 600, 1000, 1500].forEach((delay) => {
						setTimeout(() => {
							_this.handleReflow();
						}, delay);
					});
				}
			};
			document.addEventListener('click', this.onDocumentClick, { passive: true });

			// Observe Judge.me or third-party review widgets for DOM updates (pagination/sorting/filtering)
			if ('MutationObserver' in window) {
				let mutationTimeout;
				this.bodyMutationObserver = new MutationObserver((mutations) => {
					let shouldHandle = false;
					for (let i = 0; i < mutations.length; i++) {
						let target = mutations[i].target;
						if (target && target.closest && target.closest('.jdgm-review-widget, .jdgm-rev-widg, [class*="jdgm"], #judgeme_product_reviews')) {
							shouldHandle = true;
							break;
						}
					}
					if (shouldHandle) {
						clearTimeout(mutationTimeout);
						mutationTimeout = setTimeout(() => {
							_this.handleReflow();
						}, 100);
					}
				});
				this.bodyMutationObserver.observe(document.body, { childList: true, subtree: true });
			}

			this.onResize = () => {
				_this.handleReflow();
			};
			window.addEventListener('resize', this.onResize, { passive: true });
		}

		videoPlay(video_container) {
			if (!video_container) return;
			setTimeout(() => {
				let iframe = video_container.querySelector('iframe');
				let video = video_container.querySelector('video');
				if (video) {
					this.playHostedVideo();
				} else if (iframe && iframe.contentWindow) {
					if (video_container.dataset.provider === 'youtube') {
						iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: "" }), "*");
					} else if (video_container.dataset.provider === 'vimeo') {
						iframe.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
					}
				}
			}, 10);
		}

		prepareAnimations() {
			let section = this,
					button_offset = 0;

			section.tl = gsap.timeline({
				scrollTrigger: {
					trigger: section,
					start: "top center",
					invalidateOnRefresh: true
				}
			});

			document.fonts.ready.then(function() {
				let textElements = section.querySelectorAll('h3, p');
				if (textElements.length > 0 && typeof SplitText !== 'undefined') {
					section.splittext = new SplitText(textElements, {
						type: 'lines, words',
						linesClass: 'line-child'
					});
				}

				if (section.querySelector('h3') && section.querySelectorAll('h3 .line-child div').length) {
					let h3_duration = 0.7 + ((section.querySelectorAll('h3 .line-child div').length - 1) * 0.05);
					section.tl.from(section.querySelectorAll('h3 .line-child div'), {
						duration: h3_duration,
						yPercent: '100',
						stagger: 0.05
					}, 0);
					button_offset += h3_duration;
				}
				if (section.querySelector('p') && section.querySelectorAll('p .line-child div').length) {
					let p_duration = 0.7 + ((section.querySelectorAll('p .line-child div').length - 1) * 0.02);
					section.tl.from(section.querySelectorAll('p .line-child div'), {
						duration: p_duration,
						yPercent: '100',
						stagger: 0.02
					}, 0);
					button_offset += p_duration;
				}
				if (section.querySelector('.video-lightbox-modal__button')) {
					section.tl.fromTo(section.querySelector('.video-lightbox-modal__button'), {
						autoAlpha: 0
					}, {
						duration: 0.5,
						autoAlpha: 1
					}, button_offset * 0.4);
				}
			});
		}
	}
	customElements.define('background-video', BackgroundVideo);
}
